import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Invoice logo — a single binary blob per practice. The server streams the
 * raw bytes on GET /api/settings/billing/logo, but that route is
 * Bearer-token-gated; the browser doesn't attach the JWT to a plain
 * <img src>, so the preview must route through the authenticated axios
 * client and use a blob: URL.
 */

export interface LogoStatus {
    hasValue: boolean;
    contentType?: string;
    originalName?: string;
    uploadedAt?: string;
}

export function useLogoStatus() {
    return useQuery({
        queryKey: ['practice-logo'] as const,
        queryFn: async (): Promise<LogoStatus> => {
            const res = await api.get<LogoStatus>('/settings/billing/logo/status');
            return res.data;
        },
        staleTime: 60_000,
    });
}

// Fetches the logo bytes through the authenticated axios client and returns
// an object URL suitable for <img src>. Returns null while loading or when
// no logo is stored. Re-runs (and revokes the old URL) whenever cacheKey
// changes — pass the upload timestamp so a replace refetches instead of
// serving stale bytes.
export function useLogoBlobUrl(hasLogo: boolean, cacheKey?: string): string | null {
    const [src, setSrc] = useState<string | null>(null);
    useEffect(() => {
        if (!hasLogo) {
            setSrc(null);
            return;
        }
        let cancelled = false;
        let objectUrl: string | null = null;
        api.get<Blob>('/settings/billing/logo', { responseType: 'blob' })
            .then((res) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(res.data);
                setSrc(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setSrc(null);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [hasLogo, cacheKey]);
    return src;
}

export function useUploadLogo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (file: File): Promise<LogoStatus> => {
            const body = new FormData();
            body.append('file', file);
            const res = await api.post<LogoStatus>('/settings/billing/logo', body, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['practice-logo'] }),
    });
}

export function useDeleteLogo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (): Promise<void> => {
            await api.delete('/settings/billing/logo');
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['practice-logo'] }),
    });
}
